import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    BulkResolveUserDetailsModel,
    BulkResolveUserDetailsParams,
    BulkResolveUsersParams,
    GetCurrentUserDetailsParams,
    GetCurrentUserParams,
    GetCurrentUsersActivityLogParams,
    GetSupportedCultureCodesResponse,
    GetUserActivityLogByIdParams,
    GetUserActivityLogParams,
    GetUserActivityResponse,
    GetUserByIdParams,
    GetUserClientsResponse,
    GetUserDetailsByIdParams,
    GetUserDetailsResponse,
    GetUserGroupsResponse,
    GetUserResponse,
    GetUserRolesResponse,
    GetUsersParams,
    GetUsersResponse,
    InviteUserModel,
    PreRegistrationModel,
    ProblemDetails,
    ResolveUserDetailsParams,
    ResolveUserDetailsResponse,
    ResolveUsersParams,
    ResolveUsersResponse,
    SearchUsersParams,
    UpdateUserModel,
    UpdateUserPasswordModel,
    UploadCurrentUserImageBody,
    UploadCurrentUserTitleImageBody,
    UploadUserImageByUserIdBody,
    UploadUserTitleImageByUserIdBody,
    UriApiResponse,
    ValidateNewUserPasswordModel,
    ValidatePasswordResponse,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Returns a list of users<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetUsersHook = () => {
    const getUsers = useCustomIDSClient<GetUsersResponse>();

    return useCallback(
        (params?: GetUsersParams, signal?: AbortSignal) => {
            return getUsers({ url: `/users`, method: 'GET', params, signal });
        },
        [getUsers],
    );
};

export const getGetUsersQueryKey = (params?: GetUsersParams) => {
    return [`/users`, ...(params ? [params] : [])] as const;
};

export const useGetUsersQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUsersQueryKey(params);

    const getUsers = useGetUsersHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>> = ({ signal }) =>
        getUsers(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUsersQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>>;
export type GetUsersQueryError = ErrorType<ProblemDetails>;

export function useGetUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetUsersParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUsersQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the basic information of a user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetUserByIdHook = () => {
    const getUserById = useCustomIDSClient<GetUserResponse>();

    return useCallback(
        (id: string, params?: GetUserByIdParams, signal?: AbortSignal) => {
            return getUserById({ url: `/users/${id}`, method: 'GET', params, signal });
        },
        [getUserById],
    );
};

export const getGetUserByIdQueryKey = (id: string, params?: GetUserByIdParams) => {
    return [`/users/${id}`, ...(params ? [params] : [])] as const;
};

export const useGetUserByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUserByIdQueryKey(id, params);

    const getUserById = useGetUserByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>> = ({ signal }) =>
        getUserById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>>;
export type GetUserByIdQueryError = ErrorType<ProblemDetails>;

export function useGetUserById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetUserByIdParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetUserById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUserByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Deletes an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useDeleteUserByIdHook = () => {
    const deleteUserById = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteUserById({ url: `/users/${id}`, method: 'DELETE', signal });
        },
        [deleteUserById],
    );
};

export const useDeleteUserByIdMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteUserByIdHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserByIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteUserById'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteUserById = useDeleteUserByIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteUserByIdHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteUserById(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteUserByIdMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteUserByIdHook>>>>;

export type DeleteUserByIdMutationError = ErrorType<ProblemDetails>;

export const useDeleteUserById = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteUserByIdHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserByIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteUserByIdMutationOptions(options), queryClient);
};
/**
 * Updates an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useUpdateUserHook = () => {
    const updateUser = useCustomIDSClient<GetUserResponse>();

    return useCallback(
        (id: string, updateUserModel: UpdateUserModel, signal?: AbortSignal) => {
            return updateUser({
                url: `/users/${id}`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: updateUserModel,
                signal,
            });
        },
        [updateUser],
    );
};

export const useUpdateUserMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateUserHook>>>,
        TError,
        { id: string; data: UpdateUserModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateUserHook>>>,
    TError,
    { id: string; data: UpdateUserModel },
    TContext
> => {
    const mutationKey = ['updateUser'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateUser = useUpdateUserHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateUserHook>>>,
        { id: string; data: UpdateUserModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateUser(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateUserMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateUserHook>>>>;
export type UpdateUserMutationBody = UpdateUserModel;
export type UpdateUserMutationError = ErrorType<ProblemDetails>;

export const useUpdateUser = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateUserHook>>>,
            TError,
            { id: string; data: UpdateUserModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateUserHook>>>,
    TError,
    { id: string; data: UpdateUserModel },
    TContext
> => {
    return useMutation(useUpdateUserMutationOptions(options), queryClient);
};
/**
 * Activates a deactivated user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useActivateUserHook = () => {
    const activateUser = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return activateUser({ url: `/users/${id}/activate`, method: 'POST', signal });
        },
        [activateUser],
    );
};

export const useActivateUserMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useActivateUserHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useActivateUserHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['activateUser'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const activateUser = useActivateUserHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useActivateUserHook>>>, { id: string }> = (
        props,
    ) => {
        const { id } = props ?? {};

        return activateUser(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type ActivateUserMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useActivateUserHook>>>>;

export type ActivateUserMutationError = ErrorType<ProblemDetails>;

export const useActivateUser = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useActivateUserHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useActivateUserHook>>>, TError, { id: string }, TContext> => {
    return useMutation(useActivateUserMutationOptions(options), queryClient);
};
/**
 * Provides the user activity log for a user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetUserActivityLogByIdHook = () => {
    const getUserActivityLogById = useCustomIDSClient<GetUserActivityResponse>();

    return useCallback(
        (id: string, params?: GetUserActivityLogByIdParams, signal?: AbortSignal) => {
            return getUserActivityLogById({ url: `/users/${id}/activity-log`, method: 'GET', params, signal });
        },
        [getUserActivityLogById],
    );
};

export const getGetUserActivityLogByIdQueryKey = (id: string, params?: GetUserActivityLogByIdParams) => {
    return [`/users/${id}/activity-log`, ...(params ? [params] : [])] as const;
};

export const useGetUserActivityLogByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserActivityLogByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUserActivityLogByIdQueryKey(id, params);

    const getUserActivityLogById = useGetUserActivityLogByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>> = ({
        signal,
    }) => getUserActivityLogById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserActivityLogByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>
>;
export type GetUserActivityLogByIdQueryError = ErrorType<ProblemDetails>;

export function useGetUserActivityLogById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetUserActivityLogByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserActivityLogById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserActivityLogByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserActivityLogById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserActivityLogByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetUserActivityLogById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserActivityLogByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUserActivityLogByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the basic information of the clients, configured for a user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetUserClientsHook = () => {
    const getUserClients = useCustomIDSClient<GetUserClientsResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getUserClients({ url: `/users/${id}/clients`, method: 'GET', signal });
        },
        [getUserClients],
    );
};

export const getGetUserClientsQueryKey = (id: string) => {
    return [`/users/${id}/clients`] as const;
};

export const useGetUserClientsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUserClientsQueryKey(id);

    const getUserClients = useGetUserClientsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>> = ({ signal }) =>
        getUserClients(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserClientsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>>;
export type GetUserClientsQueryError = ErrorType<ProblemDetails>;

export function useGetUserClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetUserClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserClientsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUserClientsQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Deactivates an activated user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useDeactivateUserHook = () => {
    const deactivateUser = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deactivateUser({ url: `/users/${id}/deactivate`, method: 'POST', signal });
        },
        [deactivateUser],
    );
};

export const useDeactivateUserMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeactivateUserHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeactivateUserHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deactivateUser'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deactivateUser = useDeactivateUserHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeactivateUserHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deactivateUser(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeactivateUserMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeactivateUserHook>>>>;

export type DeactivateUserMutationError = ErrorType<ProblemDetails>;

export const useDeactivateUser = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeactivateUserHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeactivateUserHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeactivateUserMutationOptions(options), queryClient);
};
/**
 * Returns the detail information of a user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetUserDetailsByIdHook = () => {
    const getUserDetailsById = useCustomIDSClient<GetUserDetailsResponse>();

    return useCallback(
        (id: string, params?: GetUserDetailsByIdParams, signal?: AbortSignal) => {
            return getUserDetailsById({ url: `/users/${id}/detail`, method: 'GET', params, signal });
        },
        [getUserDetailsById],
    );
};

export const getGetUserDetailsByIdQueryKey = (id: string, params?: GetUserDetailsByIdParams) => {
    return [`/users/${id}/detail`, ...(params ? [params] : [])] as const;
};

export const useGetUserDetailsByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUserDetailsByIdQueryKey(id, params);

    const getUserDetailsById = useGetUserDetailsByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>> = ({ signal }) =>
        getUserDetailsById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserDetailsByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>
>;
export type GetUserDetailsByIdQueryError = ErrorType<ProblemDetails>;

export function useGetUserDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetUserDetailsByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetUserDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetUserDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUserDetailsByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Forces a user to reset their password on their next login<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useForcePasswordResetHook = () => {
    const forcePasswordReset = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return forcePasswordReset({ url: `/users/${id}/force-password-reset`, method: 'POST', signal });
        },
        [forcePasswordReset],
    );
};

export const useForcePasswordResetMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useForcePasswordResetHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useForcePasswordResetHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['forcePasswordReset'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const forcePasswordReset = useForcePasswordResetHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useForcePasswordResetHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return forcePasswordReset(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type ForcePasswordResetMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useForcePasswordResetHook>>>
>;

export type ForcePasswordResetMutationError = ErrorType<ProblemDetails>;

export const useForcePasswordReset = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useForcePasswordResetHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useForcePasswordResetHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useForcePasswordResetMutationOptions(options), queryClient);
};
/**
 * Returns the basic information of the groups, a user is assigned to<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetUserGroupsHook = () => {
    const getUserGroups = useCustomIDSClient<GetUserGroupsResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getUserGroups({ url: `/users/${id}/groups`, method: 'GET', signal });
        },
        [getUserGroups],
    );
};

export const getGetUserGroupsQueryKey = (id: string) => {
    return [`/users/${id}/groups`] as const;
};

export const useGetUserGroupsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUserGroupsQueryKey(id);

    const getUserGroups = useGetUserGroupsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>> = ({ signal }) =>
        getUserGroups(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserGroupsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>>;
export type GetUserGroupsQueryError = ErrorType<ProblemDetails>;

export function useGetUserGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetUserGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserGroupsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUserGroupsQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Enforces deletion of a soft-deleted user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useHardDeleteUserByIdHook = () => {
    const hardDeleteUserById = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return hardDeleteUserById({ url: `/users/${id}/hard-delete`, method: 'DELETE', signal });
        },
        [hardDeleteUserById],
    );
};

export const useHardDeleteUserByIdMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useHardDeleteUserByIdHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useHardDeleteUserByIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['hardDeleteUserById'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const hardDeleteUserById = useHardDeleteUserByIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useHardDeleteUserByIdHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return hardDeleteUserById(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type HardDeleteUserByIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useHardDeleteUserByIdHook>>>
>;

export type HardDeleteUserByIdMutationError = ErrorType<ProblemDetails>;

export const useHardDeleteUserById = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useHardDeleteUserByIdHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useHardDeleteUserByIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useHardDeleteUserByIdMutationOptions(options), queryClient);
};
/**
 * Uploads a user image for an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useUploadUserImageByUserIdHook = () => {
    const uploadUserImageByUserId = useCustomIDSClient<void>();

    return useCallback(
        (id: string, uploadUserImageByUserIdBody: UploadUserImageByUserIdBody, signal?: AbortSignal) => {
            const formData = new FormData();
            if (uploadUserImageByUserIdBody.File !== undefined) {
                formData.append(`File`, uploadUserImageByUserIdBody.File);
            }

            return uploadUserImageByUserId({ url: `/users/${id}/image`, method: 'PUT', data: formData, signal });
        },
        [uploadUserImageByUserId],
    );
};

export const useUploadUserImageByUserIdMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadUserImageByUserIdHook>>>,
        TError,
        { id: string; data: UploadUserImageByUserIdBody },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadUserImageByUserIdHook>>>,
    TError,
    { id: string; data: UploadUserImageByUserIdBody },
    TContext
> => {
    const mutationKey = ['uploadUserImageByUserId'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const uploadUserImageByUserId = useUploadUserImageByUserIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadUserImageByUserIdHook>>>,
        { id: string; data: UploadUserImageByUserIdBody }
    > = (props) => {
        const { id, data } = props ?? {};

        return uploadUserImageByUserId(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UploadUserImageByUserIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadUserImageByUserIdHook>>>
>;
export type UploadUserImageByUserIdMutationBody = UploadUserImageByUserIdBody;
export type UploadUserImageByUserIdMutationError = ErrorType<ProblemDetails>;

export const useUploadUserImageByUserId = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadUserImageByUserIdHook>>>,
            TError,
            { id: string; data: UploadUserImageByUserIdBody },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadUserImageByUserIdHook>>>,
    TError,
    { id: string; data: UploadUserImageByUserIdBody },
    TContext
> => {
    return useMutation(useUploadUserImageByUserIdMutationOptions(options), queryClient);
};
/**
 * Deletes a user image for an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useDeleteUserImageByUserIdHook = () => {
    const deleteUserImageByUserId = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteUserImageByUserId({ url: `/users/${id}/image`, method: 'DELETE', signal });
        },
        [deleteUserImageByUserId],
    );
};

export const useDeleteUserImageByUserIdMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteUserImageByUserIdHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserImageByUserIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteUserImageByUserId'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteUserImageByUserId = useDeleteUserImageByUserIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteUserImageByUserIdHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteUserImageByUserId(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteUserImageByUserIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserImageByUserIdHook>>>
>;

export type DeleteUserImageByUserIdMutationError = ErrorType<ProblemDetails>;

export const useDeleteUserImageByUserId = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteUserImageByUserIdHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserImageByUserIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteUserImageByUserIdMutationOptions(options), queryClient);
};
/**
 * Updates an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.confidential
 */
export const useUpdateUserPasswordHook = () => {
    const updateUserPassword = useCustomIDSClient<void>();

    return useCallback(
        (id: string, updateUserPasswordModel: UpdateUserPasswordModel, signal?: AbortSignal) => {
            return updateUserPassword({
                url: `/users/${id}/password`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: updateUserPasswordModel,
                signal,
            });
        },
        [updateUserPassword],
    );
};

export const useUpdateUserPasswordMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateUserPasswordHook>>>,
        TError,
        { id: string; data: UpdateUserPasswordModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateUserPasswordHook>>>,
    TError,
    { id: string; data: UpdateUserPasswordModel },
    TContext
> => {
    const mutationKey = ['updateUserPassword'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateUserPassword = useUpdateUserPasswordHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateUserPasswordHook>>>,
        { id: string; data: UpdateUserPasswordModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateUserPassword(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateUserPasswordMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateUserPasswordHook>>>
>;
export type UpdateUserPasswordMutationBody = UpdateUserPasswordModel;
export type UpdateUserPasswordMutationError = ErrorType<ProblemDetails>;

export const useUpdateUserPassword = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateUserPasswordHook>>>,
            TError,
            { id: string; data: UpdateUserPasswordModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateUserPasswordHook>>>,
    TError,
    { id: string; data: UpdateUserPasswordModel },
    TContext
> => {
    return useMutation(useUpdateUserPasswordMutationOptions(options), queryClient);
};
/**
 * Resends the invitation of a user or returns the url if no email is set<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useResendInvitationHook = () => {
    const resendInvitation = useCustomIDSClient<UriApiResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return resendInvitation({ url: `/users/${id}/resend-invitation`, method: 'POST', signal });
        },
        [resendInvitation],
    );
};

export const useResendInvitationMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['resendInvitation'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const resendInvitation = useResendInvitationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return resendInvitation(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type ResendInvitationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>
>;

export type ResendInvitationMutationError = ErrorType<ProblemDetails>;

export const useResendInvitation = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useResendInvitationMutationOptions(options), queryClient);
};
/**
 * Sends a reset link to a user, or generates and returns the url if user has no email configured<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useResetPasswordHook = () => {
    const resetPassword = useCustomIDSClient<UriApiResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return resetPassword({ url: `/users/${id}/reset-password`, method: 'POST', signal });
        },
        [resetPassword],
    );
};

export const useResetPasswordMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useResetPasswordHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useResetPasswordHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['resetPassword'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const resetPassword = useResetPasswordHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useResetPasswordHook>>>, { id: string }> = (
        props,
    ) => {
        const { id } = props ?? {};

        return resetPassword(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type ResetPasswordMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useResetPasswordHook>>>>;

export type ResetPasswordMutationError = ErrorType<ProblemDetails>;

export const useResetPassword = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useResetPasswordHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useResetPasswordHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useResetPasswordMutationOptions(options), queryClient);
};
/**
 * Returns the basic information of the roles, that are assigned to a user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetUserRolesHook = () => {
    const getUserRoles = useCustomIDSClient<GetUserRolesResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getUserRoles({ url: `/users/${id}/roles`, method: 'GET', signal });
        },
        [getUserRoles],
    );
};

export const getGetUserRolesQueryKey = (id: string) => {
    return [`/users/${id}/roles`] as const;
};

export const useGetUserRolesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUserRolesQueryKey(id);

    const getUserRoles = useGetUserRolesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>> = ({ signal }) =>
        getUserRoles(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserRolesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>>;
export type GetUserRolesQueryError = ErrorType<ProblemDetails>;

export function useGetUserRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetUserRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserRolesHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUserRolesQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Uploads a title image for an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useUploadUserTitleImageByUserIdHook = () => {
    const uploadUserTitleImageByUserId = useCustomIDSClient<void>();

    return useCallback(
        (id: string, uploadUserTitleImageByUserIdBody: UploadUserTitleImageByUserIdBody, signal?: AbortSignal) => {
            const formData = new FormData();
            if (uploadUserTitleImageByUserIdBody.File !== undefined) {
                formData.append(`File`, uploadUserTitleImageByUserIdBody.File);
            }

            return uploadUserTitleImageByUserId({
                url: `/users/${id}/title-image`,
                method: 'PUT',
                data: formData,
                signal,
            });
        },
        [uploadUserTitleImageByUserId],
    );
};

export const useUploadUserTitleImageByUserIdMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadUserTitleImageByUserIdHook>>>,
        TError,
        { id: string; data: UploadUserTitleImageByUserIdBody },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadUserTitleImageByUserIdHook>>>,
    TError,
    { id: string; data: UploadUserTitleImageByUserIdBody },
    TContext
> => {
    const mutationKey = ['uploadUserTitleImageByUserId'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const uploadUserTitleImageByUserId = useUploadUserTitleImageByUserIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadUserTitleImageByUserIdHook>>>,
        { id: string; data: UploadUserTitleImageByUserIdBody }
    > = (props) => {
        const { id, data } = props ?? {};

        return uploadUserTitleImageByUserId(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UploadUserTitleImageByUserIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadUserTitleImageByUserIdHook>>>
>;
export type UploadUserTitleImageByUserIdMutationBody = UploadUserTitleImageByUserIdBody;
export type UploadUserTitleImageByUserIdMutationError = ErrorType<ProblemDetails>;

export const useUploadUserTitleImageByUserId = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadUserTitleImageByUserIdHook>>>,
            TError,
            { id: string; data: UploadUserTitleImageByUserIdBody },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadUserTitleImageByUserIdHook>>>,
    TError,
    { id: string; data: UploadUserTitleImageByUserIdBody },
    TContext
> => {
    return useMutation(useUploadUserTitleImageByUserIdMutationOptions(options), queryClient);
};
/**
 * Deletes a user title image for an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useDeleteUserTitleImageByUserIdHook = () => {
    const deleteUserTitleImageByUserId = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteUserTitleImageByUserId({ url: `/users/${id}/title-image`, method: 'DELETE', signal });
        },
        [deleteUserTitleImageByUserId],
    );
};

export const useDeleteUserTitleImageByUserIdMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteUserTitleImageByUserIdHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserTitleImageByUserIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteUserTitleImageByUserId'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteUserTitleImageByUserId = useDeleteUserTitleImageByUserIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteUserTitleImageByUserIdHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteUserTitleImageByUserId(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteUserTitleImageByUserIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserTitleImageByUserIdHook>>>
>;

export type DeleteUserTitleImageByUserIdMutationError = ErrorType<ProblemDetails>;

export const useDeleteUserTitleImageByUserId = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteUserTitleImageByUserIdHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserTitleImageByUserIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteUserTitleImageByUserIdMutationOptions(options), queryClient);
};
/**
 * Undeletes an soft-deleted user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useUndeleteUserHook = () => {
    const undeleteUser = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return undeleteUser({ url: `/users/${id}/undelete`, method: 'POST', signal });
        },
        [undeleteUser],
    );
};

export const useUndeleteUserMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUndeleteUserHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUndeleteUserHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['undeleteUser'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const undeleteUser = useUndeleteUserHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useUndeleteUserHook>>>, { id: string }> = (
        props,
    ) => {
        const { id } = props ?? {};

        return undeleteUser(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type UndeleteUserMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUndeleteUserHook>>>>;

export type UndeleteUserMutationError = ErrorType<ProblemDetails>;

export const useUndeleteUser = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUndeleteUserHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useUndeleteUserHook>>>, TError, { id: string }, TContext> => {
    return useMutation(useUndeleteUserMutationOptions(options), queryClient);
};
/**
 * Configures a client to be used by a user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useConfigureClientForUserHook = () => {
    const configureClientForUser = useCustomIDSClient<void>();

    return useCallback(
        (userId: string, clientId: string, signal?: AbortSignal) => {
            return configureClientForUser({ url: `/users/${userId}/clients/${clientId}`, method: 'POST', signal });
        },
        [configureClientForUser],
    );
};

export const useConfigureClientForUserMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useConfigureClientForUserHook>>>,
        TError,
        { userId: string; clientId: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useConfigureClientForUserHook>>>,
    TError,
    { userId: string; clientId: string },
    TContext
> => {
    const mutationKey = ['configureClientForUser'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const configureClientForUser = useConfigureClientForUserHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useConfigureClientForUserHook>>>,
        { userId: string; clientId: string }
    > = (props) => {
        const { userId, clientId } = props ?? {};

        return configureClientForUser(userId, clientId);
    };

    return { mutationFn, ...mutationOptions };
};

export type ConfigureClientForUserMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useConfigureClientForUserHook>>>
>;

export type ConfigureClientForUserMutationError = ErrorType<ProblemDetails>;

export const useConfigureClientForUser = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useConfigureClientForUserHook>>>,
            TError,
            { userId: string; clientId: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useConfigureClientForUserHook>>>,
    TError,
    { userId: string; clientId: string },
    TContext
> => {
    return useMutation(useConfigureClientForUserMutationOptions(options), queryClient);
};
/**
 * Provides the user activity log for all users<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetUserActivityLogHook = () => {
    const getUserActivityLog = useCustomIDSClient<GetUserActivityResponse>();

    return useCallback(
        (params?: GetUserActivityLogParams, signal?: AbortSignal) => {
            return getUserActivityLog({ url: `/users/activity-log`, method: 'GET', params, signal });
        },
        [getUserActivityLog],
    );
};

export const getGetUserActivityLogQueryKey = (params?: GetUserActivityLogParams) => {
    return [`/users/activity-log`, ...(params ? [params] : [])] as const;
};

export const useGetUserActivityLogQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetUserActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUserActivityLogQueryKey(params);

    const getUserActivityLog = useGetUserActivityLogHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>> = ({ signal }) =>
        getUserActivityLog(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserActivityLogQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>
>;
export type GetUserActivityLogQueryError = ErrorType<ProblemDetails>;

export function useGetUserActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetUserActivityLogParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetUserActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetUserActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetUserActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetUserActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserActivityLogHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUserActivityLogQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns a list of all supported culture codes
 */
export const useGetSupportedCultureCodesHook = () => {
    const getSupportedCultureCodes = useCustomIDSClient<GetSupportedCultureCodesResponse>();

    return useCallback(
        (signal?: AbortSignal) => {
            return getSupportedCultureCodes({ url: `/users/culture-codes`, method: 'GET', signal });
        },
        [getSupportedCultureCodes],
    );
};

export const getGetSupportedCultureCodesQueryKey = () => {
    return [`/users/culture-codes`] as const;
};

export const useGetSupportedCultureCodesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(options?: {
    query?: Partial<
        UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>, TError, TData>
    >;
}) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetSupportedCultureCodesQueryKey();

    const getSupportedCultureCodes = useGetSupportedCultureCodesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>> = ({
        signal,
    }) => getSupportedCultureCodes(signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSupportedCultureCodesQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>
>;
export type GetSupportedCultureCodesQueryError = ErrorType<ProblemDetails>;

export function useGetSupportedCultureCodes<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSupportedCultureCodes<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSupportedCultureCodes<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetSupportedCultureCodes<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSupportedCultureCodesHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetSupportedCultureCodesQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Invites a new user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useInviteUserHook = () => {
    const inviteUser = useCustomIDSClient<UriApiResponse>();

    return useCallback(
        (inviteUserModel: InviteUserModel, signal?: AbortSignal) => {
            return inviteUser({
                url: `/users/invite`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: inviteUserModel,
                signal,
            });
        },
        [inviteUser],
    );
};

export const useInviteUserMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
        TError,
        { data: InviteUserModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
    TError,
    { data: InviteUserModel },
    TContext
> => {
    const mutationKey = ['inviteUser'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const inviteUser = useInviteUserHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
        { data: InviteUserModel }
    > = (props) => {
        const { data } = props ?? {};

        return inviteUser(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type InviteUserMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>>;
export type InviteUserMutationBody = InviteUserModel;
export type InviteUserMutationError = ErrorType<ProblemDetails>;

export const useInviteUser = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
            TError,
            { data: InviteUserModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
    TError,
    { data: InviteUserModel },
    TContext
> => {
    return useMutation(useInviteUserMutationOptions(options), queryClient);
};
/**
 * Returns the basic information of the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetCurrentUserHook = () => {
    const getCurrentUser = useCustomIDSClient<GetUserResponse>();

    return useCallback(
        (params?: GetCurrentUserParams, signal?: AbortSignal) => {
            return getCurrentUser({ url: `/users/me`, method: 'GET', params, signal });
        },
        [getCurrentUser],
    );
};

export const getGetCurrentUserQueryKey = (params?: GetCurrentUserParams) => {
    return [`/users/me`, ...(params ? [params] : [])] as const;
};

export const useGetCurrentUserQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUserParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetCurrentUserQueryKey(params);

    const getCurrentUser = useGetCurrentUserHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>> = ({ signal }) =>
        getCurrentUser(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCurrentUserQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>>;
export type GetCurrentUserQueryError = ErrorType<ProblemDetails>;

export function useGetCurrentUser<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetCurrentUserParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUser<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUserParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUser<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUserParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetCurrentUser<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUserParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetCurrentUserQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Deletes the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useDeleteCurrentUserHook = () => {
    const deleteCurrentUser = useCustomIDSClient<void>();

    return useCallback(
        (signal?: AbortSignal) => {
            return deleteCurrentUser({ url: `/users/me`, method: 'DELETE', signal });
        },
        [deleteCurrentUser],
    );
};

export const useDeleteCurrentUserMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserHook>>>,
        TError,
        void,
        TContext
    >;
}): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserHook>>>, TError, void, TContext> => {
    const mutationKey = ['deleteCurrentUser'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteCurrentUser = useDeleteCurrentUserHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserHook>>>, void> = () => {
        return deleteCurrentUser();
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteCurrentUserMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserHook>>>
>;

export type DeleteCurrentUserMutationError = ErrorType<ProblemDetails>;

export const useDeleteCurrentUser = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserHook>>>,
            TError,
            void,
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserHook>>>, TError, void, TContext> => {
    return useMutation(useDeleteCurrentUserMutationOptions(options), queryClient);
};
/**
 * Updates an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useUpdateCurrentUserHook = () => {
    const updateCurrentUser = useCustomIDSClient<GetUserResponse>();

    return useCallback(
        (updateUserModel: UpdateUserModel, signal?: AbortSignal) => {
            return updateCurrentUser({
                url: `/users/me`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: updateUserModel,
                signal,
            });
        },
        [updateCurrentUser],
    );
};

export const useUpdateCurrentUserMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserHook>>>,
        TError,
        { data: UpdateUserModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserHook>>>,
    TError,
    { data: UpdateUserModel },
    TContext
> => {
    const mutationKey = ['updateCurrentUser'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateCurrentUser = useUpdateCurrentUserHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserHook>>>,
        { data: UpdateUserModel }
    > = (props) => {
        const { data } = props ?? {};

        return updateCurrentUser(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateCurrentUserMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserHook>>>
>;
export type UpdateCurrentUserMutationBody = UpdateUserModel;
export type UpdateCurrentUserMutationError = ErrorType<ProblemDetails>;

export const useUpdateCurrentUser = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserHook>>>,
            TError,
            { data: UpdateUserModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserHook>>>,
    TError,
    { data: UpdateUserModel },
    TContext
> => {
    return useMutation(useUpdateCurrentUserMutationOptions(options), queryClient);
};
/**
 * Provides the user activity log for the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetCurrentUsersActivityLogHook = () => {
    const getCurrentUsersActivityLog = useCustomIDSClient<GetUserActivityResponse>();

    return useCallback(
        (params?: GetCurrentUsersActivityLogParams, signal?: AbortSignal) => {
            return getCurrentUsersActivityLog({ url: `/users/me/activity-log`, method: 'GET', params, signal });
        },
        [getCurrentUsersActivityLog],
    );
};

export const getGetCurrentUsersActivityLogQueryKey = (params?: GetCurrentUsersActivityLogParams) => {
    return [`/users/me/activity-log`, ...(params ? [params] : [])] as const;
};

export const useGetCurrentUsersActivityLogQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUsersActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetCurrentUsersActivityLogQueryKey(params);

    const getCurrentUsersActivityLog = useGetCurrentUsersActivityLogHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>> = ({
        signal,
    }) => getCurrentUsersActivityLog(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCurrentUsersActivityLogQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>
>;
export type GetCurrentUsersActivityLogQueryError = ErrorType<ProblemDetails>;

export function useGetCurrentUsersActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetCurrentUsersActivityLogParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUsersActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUsersActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUsersActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUsersActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetCurrentUsersActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUsersActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUsersActivityLogHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetCurrentUsersActivityLogQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the detail information of the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useGetCurrentUserDetailsHook = () => {
    const getCurrentUserDetails = useCustomIDSClient<GetUserDetailsResponse>();

    return useCallback(
        (params?: GetCurrentUserDetailsParams, signal?: AbortSignal) => {
            return getCurrentUserDetails({ url: `/users/me/detail`, method: 'GET', params, signal });
        },
        [getCurrentUserDetails],
    );
};

export const getGetCurrentUserDetailsQueryKey = (params?: GetCurrentUserDetailsParams) => {
    return [`/users/me/detail`, ...(params ? [params] : [])] as const;
};

export const useGetCurrentUserDetailsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUserDetailsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetCurrentUserDetailsQueryKey(params);

    const getCurrentUserDetails = useGetCurrentUserDetailsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>> = ({ signal }) =>
        getCurrentUserDetails(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCurrentUserDetailsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>
>;
export type GetCurrentUserDetailsQueryError = ErrorType<ProblemDetails>;

export function useGetCurrentUserDetails<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetCurrentUserDetailsParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUserDetails<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUserDetailsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUserDetails<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUserDetailsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetCurrentUserDetails<
    TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetCurrentUserDetailsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCurrentUserDetailsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetCurrentUserDetailsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Uploads a user image for the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useUploadCurrentUserImageHook = () => {
    const uploadCurrentUserImage = useCustomIDSClient<void>();

    return useCallback(
        (uploadCurrentUserImageBody: UploadCurrentUserImageBody, signal?: AbortSignal) => {
            const formData = new FormData();
            if (uploadCurrentUserImageBody.File !== undefined) {
                formData.append(`File`, uploadCurrentUserImageBody.File);
            }

            return uploadCurrentUserImage({ url: `/users/me/image`, method: 'PUT', data: formData, signal });
        },
        [uploadCurrentUserImage],
    );
};

export const useUploadCurrentUserImageMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserImageHook>>>,
        TError,
        { data: UploadCurrentUserImageBody },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserImageHook>>>,
    TError,
    { data: UploadCurrentUserImageBody },
    TContext
> => {
    const mutationKey = ['uploadCurrentUserImage'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const uploadCurrentUserImage = useUploadCurrentUserImageHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserImageHook>>>,
        { data: UploadCurrentUserImageBody }
    > = (props) => {
        const { data } = props ?? {};

        return uploadCurrentUserImage(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UploadCurrentUserImageMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserImageHook>>>
>;
export type UploadCurrentUserImageMutationBody = UploadCurrentUserImageBody;
export type UploadCurrentUserImageMutationError = ErrorType<ProblemDetails>;

export const useUploadCurrentUserImage = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserImageHook>>>,
            TError,
            { data: UploadCurrentUserImageBody },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserImageHook>>>,
    TError,
    { data: UploadCurrentUserImageBody },
    TContext
> => {
    return useMutation(useUploadCurrentUserImageMutationOptions(options), queryClient);
};
/**
 * Deletes a user image for the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useDeleteCurrentUserImageHook = () => {
    const deleteCurrentUserImage = useCustomIDSClient<void>();

    return useCallback(
        (signal?: AbortSignal) => {
            return deleteCurrentUserImage({ url: `/users/me/image`, method: 'DELETE', signal });
        },
        [deleteCurrentUserImage],
    );
};

export const useDeleteCurrentUserImageMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserImageHook>>>,
        TError,
        void,
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserImageHook>>>,
    TError,
    void,
    TContext
> => {
    const mutationKey = ['deleteCurrentUserImage'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteCurrentUserImage = useDeleteCurrentUserImageHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserImageHook>>>,
        void
    > = () => {
        return deleteCurrentUserImage();
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteCurrentUserImageMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserImageHook>>>
>;

export type DeleteCurrentUserImageMutationError = ErrorType<ProblemDetails>;

export const useDeleteCurrentUserImage = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserImageHook>>>,
            TError,
            void,
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserImageHook>>>, TError, void, TContext> => {
    return useMutation(useDeleteCurrentUserImageMutationOptions(options), queryClient);
};
/**
 * Updates an existing user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.confidential
 */
export const useUpdateCurrentUserPasswordHook = () => {
    const updateCurrentUserPassword = useCustomIDSClient<void>();

    return useCallback(
        (updateUserPasswordModel: UpdateUserPasswordModel, signal?: AbortSignal) => {
            return updateCurrentUserPassword({
                url: `/users/me/password`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: updateUserPasswordModel,
                signal,
            });
        },
        [updateCurrentUserPassword],
    );
};

export const useUpdateCurrentUserPasswordMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserPasswordHook>>>,
        TError,
        { data: UpdateUserPasswordModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserPasswordHook>>>,
    TError,
    { data: UpdateUserPasswordModel },
    TContext
> => {
    const mutationKey = ['updateCurrentUserPassword'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateCurrentUserPassword = useUpdateCurrentUserPasswordHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserPasswordHook>>>,
        { data: UpdateUserPasswordModel }
    > = (props) => {
        const { data } = props ?? {};

        return updateCurrentUserPassword(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateCurrentUserPasswordMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserPasswordHook>>>
>;
export type UpdateCurrentUserPasswordMutationBody = UpdateUserPasswordModel;
export type UpdateCurrentUserPasswordMutationError = ErrorType<ProblemDetails>;

export const useUpdateCurrentUserPassword = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserPasswordHook>>>,
            TError,
            { data: UpdateUserPasswordModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateCurrentUserPasswordHook>>>,
    TError,
    { data: UpdateUserPasswordModel },
    TContext
> => {
    return useMutation(useUpdateCurrentUserPasswordMutationOptions(options), queryClient);
};
/**
 * Uploads a title image for the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useUploadCurrentUserTitleImageHook = () => {
    const uploadCurrentUserTitleImage = useCustomIDSClient<void>();

    return useCallback(
        (uploadCurrentUserTitleImageBody: UploadCurrentUserTitleImageBody, signal?: AbortSignal) => {
            const formData = new FormData();
            if (uploadCurrentUserTitleImageBody.File !== undefined) {
                formData.append(`File`, uploadCurrentUserTitleImageBody.File);
            }

            return uploadCurrentUserTitleImage({ url: `/users/me/title-image`, method: 'PUT', data: formData, signal });
        },
        [uploadCurrentUserTitleImage],
    );
};

export const useUploadCurrentUserTitleImageMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserTitleImageHook>>>,
        TError,
        { data: UploadCurrentUserTitleImageBody },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserTitleImageHook>>>,
    TError,
    { data: UploadCurrentUserTitleImageBody },
    TContext
> => {
    const mutationKey = ['uploadCurrentUserTitleImage'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const uploadCurrentUserTitleImage = useUploadCurrentUserTitleImageHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserTitleImageHook>>>,
        { data: UploadCurrentUserTitleImageBody }
    > = (props) => {
        const { data } = props ?? {};

        return uploadCurrentUserTitleImage(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UploadCurrentUserTitleImageMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserTitleImageHook>>>
>;
export type UploadCurrentUserTitleImageMutationBody = UploadCurrentUserTitleImageBody;
export type UploadCurrentUserTitleImageMutationError = ErrorType<ProblemDetails>;

export const useUploadCurrentUserTitleImage = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserTitleImageHook>>>,
            TError,
            { data: UploadCurrentUserTitleImageBody },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadCurrentUserTitleImageHook>>>,
    TError,
    { data: UploadCurrentUserTitleImageBody },
    TContext
> => {
    return useMutation(useUploadCurrentUserTitleImageMutationOptions(options), queryClient);
};
/**
 * Deletes a user title image for the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useDeleteCurrentUserTitleImageHook = () => {
    const deleteCurrentUserTitleImage = useCustomIDSClient<void>();

    return useCallback(
        (signal?: AbortSignal) => {
            return deleteCurrentUserTitleImage({ url: `/users/me/title-image`, method: 'DELETE', signal });
        },
        [deleteCurrentUserTitleImage],
    );
};

export const useDeleteCurrentUserTitleImageMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserTitleImageHook>>>,
        TError,
        void,
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserTitleImageHook>>>,
    TError,
    void,
    TContext
> => {
    const mutationKey = ['deleteCurrentUserTitleImage'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteCurrentUserTitleImage = useDeleteCurrentUserTitleImageHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserTitleImageHook>>>,
        void
    > = () => {
        return deleteCurrentUserTitleImage();
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteCurrentUserTitleImageMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserTitleImageHook>>>
>;

export type DeleteCurrentUserTitleImageMutationError = ErrorType<ProblemDetails>;

export const useDeleteCurrentUserTitleImage = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserTitleImageHook>>>,
            TError,
            void,
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteCurrentUserTitleImageHook>>>,
    TError,
    void,
    TContext
> => {
    return useMutation(useDeleteCurrentUserTitleImageMutationOptions(options), queryClient);
};
/**
 * Validates if a password matches the criteria for the current user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.confidential
 */
export const useValidatePasswordHook = () => {
    const validatePassword = useCustomIDSClient<ValidatePasswordResponse>();

    return useCallback(
        (validatePasswordBody: string, signal?: AbortSignal) => {
            return validatePassword({
                url: `/users/me/validate-password`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: validatePasswordBody,
                signal,
            });
        },
        [validatePassword],
    );
};

export const useValidatePasswordMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useValidatePasswordHook>>>,
        TError,
        { data: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useValidatePasswordHook>>>,
    TError,
    { data: string },
    TContext
> => {
    const mutationKey = ['validatePassword'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const validatePassword = useValidatePasswordHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useValidatePasswordHook>>>,
        { data: string }
    > = (props) => {
        const { data } = props ?? {};

        return validatePassword(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type ValidatePasswordMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useValidatePasswordHook>>>
>;
export type ValidatePasswordMutationBody = string;
export type ValidatePasswordMutationError = ErrorType<ProblemDetails>;

export const useValidatePassword = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useValidatePasswordHook>>>,
            TError,
            { data: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useValidatePasswordHook>>>,
    TError,
    { data: string },
    TContext
> => {
    return useMutation(useValidatePasswordMutationOptions(options), queryClient);
};
/**
 * Validates if a password matches the criteria without checking a users password history
 */
export const useValidatePasswordUnauthorizedHook = () => {
    const validatePasswordUnauthorized = useCustomIDSClient<ValidatePasswordResponse>();

    return useCallback(
        (validateNewUserPasswordModel: ValidateNewUserPasswordModel, signal?: AbortSignal) => {
            return validatePasswordUnauthorized({
                url: `/users/new/validate-password`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: validateNewUserPasswordModel,
                signal,
            });
        },
        [validatePasswordUnauthorized],
    );
};

export const useValidatePasswordUnauthorizedMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useValidatePasswordUnauthorizedHook>>>,
        TError,
        { data: ValidateNewUserPasswordModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useValidatePasswordUnauthorizedHook>>>,
    TError,
    { data: ValidateNewUserPasswordModel },
    TContext
> => {
    const mutationKey = ['validatePasswordUnauthorized'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const validatePasswordUnauthorized = useValidatePasswordUnauthorizedHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useValidatePasswordUnauthorizedHook>>>,
        { data: ValidateNewUserPasswordModel }
    > = (props) => {
        const { data } = props ?? {};

        return validatePasswordUnauthorized(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type ValidatePasswordUnauthorizedMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useValidatePasswordUnauthorizedHook>>>
>;
export type ValidatePasswordUnauthorizedMutationBody = ValidateNewUserPasswordModel;
export type ValidatePasswordUnauthorizedMutationError = ErrorType<ProblemDetails>;

export const useValidatePasswordUnauthorized = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useValidatePasswordUnauthorizedHook>>>,
            TError,
            { data: ValidateNewUserPasswordModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useValidatePasswordUnauthorizedHook>>>,
    TError,
    { data: ValidateNewUserPasswordModel },
    TContext
> => {
    return useMutation(useValidatePasswordUnauthorizedMutationOptions(options), queryClient);
};
/**
 * Creates or updates a pre registration for a user<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.write
 */
export const useCreateOrUpdatePreRegistrationHook = () => {
    const createOrUpdatePreRegistration = useCustomIDSClient<void>();

    return useCallback(
        (preRegistrationModel: PreRegistrationModel, signal?: AbortSignal) => {
            return createOrUpdatePreRegistration({
                url: `/users/pre-registration`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: preRegistrationModel,
                signal,
            });
        },
        [createOrUpdatePreRegistration],
    );
};

export const useCreateOrUpdatePreRegistrationMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateOrUpdatePreRegistrationHook>>>,
        TError,
        { data: PreRegistrationModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateOrUpdatePreRegistrationHook>>>,
    TError,
    { data: PreRegistrationModel },
    TContext
> => {
    const mutationKey = ['createOrUpdatePreRegistration'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createOrUpdatePreRegistration = useCreateOrUpdatePreRegistrationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateOrUpdatePreRegistrationHook>>>,
        { data: PreRegistrationModel }
    > = (props) => {
        const { data } = props ?? {};

        return createOrUpdatePreRegistration(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateOrUpdatePreRegistrationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateOrUpdatePreRegistrationHook>>>
>;
export type CreateOrUpdatePreRegistrationMutationBody = PreRegistrationModel;
export type CreateOrUpdatePreRegistrationMutationError = ErrorType<ProblemDetails>;

export const useCreateOrUpdatePreRegistration = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateOrUpdatePreRegistrationHook>>>,
            TError,
            { data: PreRegistrationModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateOrUpdatePreRegistrationHook>>>,
    TError,
    { data: PreRegistrationModel },
    TContext
> => {
    return useMutation(useCreateOrUpdatePreRegistrationMutationOptions(options), queryClient);
};
/**
 * Resolves user ids to its corresponding models<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useResolveUsersHook = () => {
    const resolveUsers = useCustomIDSClient<ResolveUsersResponse>();

    return useCallback(
        (params: ResolveUsersParams, signal?: AbortSignal) => {
            return resolveUsers({ url: `/users/resolve`, method: 'GET', params, signal });
        },
        [resolveUsers],
    );
};

export const getResolveUsersQueryKey = (params?: ResolveUsersParams) => {
    return [`/users/resolve`, ...(params ? [params] : [])] as const;
};

export const useResolveUsersQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolveUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getResolveUsersQueryKey(params);

    const resolveUsers = useResolveUsersHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>> = ({ signal }) =>
        resolveUsers(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ResolveUsersQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>>;
export type ResolveUsersQueryError = ErrorType<ProblemDetails>;

export function useResolveUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolveUsersParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolveUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolveUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolveUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolveUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useResolveUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: ResolveUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUsersHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useResolveUsersQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Resolves user ids to its corresponding models<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useBulkResolveUsersHook = () => {
    const bulkResolveUsers = useCustomIDSClient<ResolveUsersResponse>();

    return useCallback(
        (bulkResolveUsersBody: string[], params?: BulkResolveUsersParams, signal?: AbortSignal) => {
            return bulkResolveUsers({
                url: `/users/resolve`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: bulkResolveUsersBody,
                params,
                signal,
            });
        },
        [bulkResolveUsers],
    );
};

export const useBulkResolveUsersMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useBulkResolveUsersHook>>>,
        TError,
        { data: string[]; params?: BulkResolveUsersParams },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useBulkResolveUsersHook>>>,
    TError,
    { data: string[]; params?: BulkResolveUsersParams },
    TContext
> => {
    const mutationKey = ['bulkResolveUsers'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const bulkResolveUsers = useBulkResolveUsersHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useBulkResolveUsersHook>>>,
        { data: string[]; params?: BulkResolveUsersParams }
    > = (props) => {
        const { data, params } = props ?? {};

        return bulkResolveUsers(data, params);
    };

    return { mutationFn, ...mutationOptions };
};

export type BulkResolveUsersMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useBulkResolveUsersHook>>>
>;
export type BulkResolveUsersMutationBody = string[];
export type BulkResolveUsersMutationError = ErrorType<ProblemDetails>;

export const useBulkResolveUsers = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useBulkResolveUsersHook>>>,
            TError,
            { data: string[]; params?: BulkResolveUsersParams },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useBulkResolveUsersHook>>>,
    TError,
    { data: string[]; params?: BulkResolveUsersParams },
    TContext
> => {
    return useMutation(useBulkResolveUsersMutationOptions(options), queryClient);
};
/**
 * Resolves user ids or emails to its corresponding detail models<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useResolveUserDetailsHook = () => {
    const resolveUserDetails = useCustomIDSClient<ResolveUserDetailsResponse>();

    return useCallback(
        (params?: ResolveUserDetailsParams, signal?: AbortSignal) => {
            return resolveUserDetails({ url: `/users/resolve/detail`, method: 'GET', params, signal });
        },
        [resolveUserDetails],
    );
};

export const getResolveUserDetailsQueryKey = (params?: ResolveUserDetailsParams) => {
    return [`/users/resolve/detail`, ...(params ? [params] : [])] as const;
};

export const useResolveUserDetailsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: ResolveUserDetailsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getResolveUserDetailsQueryKey(params);

    const resolveUserDetails = useResolveUserDetailsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>> = ({ signal }) =>
        resolveUserDetails(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ResolveUserDetailsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>
>;
export type ResolveUserDetailsQueryError = ErrorType<ProblemDetails>;

export function useResolveUserDetails<
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | ResolveUserDetailsParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolveUserDetails<
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: ResolveUserDetailsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolveUserDetails<
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: ResolveUserDetailsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useResolveUserDetails<
    TData = Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: ResolveUserDetailsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useResolveUserDetailsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useResolveUserDetailsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Resolves user ids or emails to its corresponding detail models<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useBulkResolveUserDetailsHook = () => {
    const bulkResolveUserDetails = useCustomIDSClient<ResolveUserDetailsResponse>();

    return useCallback(
        (
            bulkResolveUserDetailsModel: BulkResolveUserDetailsModel,
            params?: BulkResolveUserDetailsParams,
            signal?: AbortSignal,
        ) => {
            return bulkResolveUserDetails({
                url: `/users/resolve/detail`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: bulkResolveUserDetailsModel,
                params,
                signal,
            });
        },
        [bulkResolveUserDetails],
    );
};

export const useBulkResolveUserDetailsMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useBulkResolveUserDetailsHook>>>,
        TError,
        { data: BulkResolveUserDetailsModel; params?: BulkResolveUserDetailsParams },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useBulkResolveUserDetailsHook>>>,
    TError,
    { data: BulkResolveUserDetailsModel; params?: BulkResolveUserDetailsParams },
    TContext
> => {
    const mutationKey = ['bulkResolveUserDetails'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const bulkResolveUserDetails = useBulkResolveUserDetailsHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useBulkResolveUserDetailsHook>>>,
        { data: BulkResolveUserDetailsModel; params?: BulkResolveUserDetailsParams }
    > = (props) => {
        const { data, params } = props ?? {};

        return bulkResolveUserDetails(data, params);
    };

    return { mutationFn, ...mutationOptions };
};

export type BulkResolveUserDetailsMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useBulkResolveUserDetailsHook>>>
>;
export type BulkResolveUserDetailsMutationBody = BulkResolveUserDetailsModel;
export type BulkResolveUserDetailsMutationError = ErrorType<ProblemDetails>;

export const useBulkResolveUserDetails = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useBulkResolveUserDetailsHook>>>,
            TError,
            { data: BulkResolveUserDetailsModel; params?: BulkResolveUserDetailsParams },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useBulkResolveUserDetailsHook>>>,
    TError,
    { data: BulkResolveUserDetailsModel; params?: BulkResolveUserDetailsParams },
    TContext
> => {
    return useMutation(useBulkResolveUserDetailsMutationOptions(options), queryClient);
};
/**
 * Returns a list of users, based on given filters<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/users.read
 */
export const useSearchUsersHook = () => {
    const searchUsers = useCustomIDSClient<GetUsersResponse>();

    return useCallback(
        (params?: SearchUsersParams, signal?: AbortSignal) => {
            return searchUsers({ url: `/users/search`, method: 'GET', params, signal });
        },
        [searchUsers],
    );
};

export const getSearchUsersQueryKey = (params?: SearchUsersParams) => {
    return [`/users/search`, ...(params ? [params] : [])] as const;
};

export const useSearchUsersQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getSearchUsersQueryKey(params);

    const searchUsers = useSearchUsersHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>> = ({ signal }) =>
        searchUsers(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchUsersQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>>;
export type SearchUsersQueryError = ErrorType<ProblemDetails>;

export function useSearchUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | SearchUsersParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useSearchUsers<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchUsersParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchUsersHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useSearchUsersQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
